/* import __COLOCATED_TEMPLATE__ from './linkified-attribute.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Session from 'embercom/services/session';
import { inject as service } from '@ember/service';

interface Args {
  value?: string | null;
  linkifiedValue?: string | null;
  edit?: (event: Event) => void;
  isDescription?: boolean;
  isTitle?: boolean;
  hasError?: boolean;
  isRequired?: boolean;
}

const URL_REGEX = /(https?:\/\/)([^\s]+)/g;
const URL_REPLACEMENT = '<a target="_blank" href="$1$2" data-inline-link="true">$2</a>';

interface Signature {
  Args: Args;
}

export default class LinkifiedAttribute extends Component<Signature> {
  @tracked clientHeight: number = document.body.clientHeight;
  @service declare session: Session;
  linkWasClicked = false;

  get isTitle() {
    return this.args.isTitle;
  }

  get inputPadding() {
    return this.isTitle ? '-mx-2 p-2' : 'py-px px-1';
  }

  get hasError() {
    return this.args.hasError;
  }

  get placeholderText() {
    if (this.args.isDescription) {
      return 'inbox.conversation-attributes.add-description-placeholder';
    }

    if (this.isTitle) {
      return 'inbox.conversation-attributes.add-title-placeholder';
    }

    return 'inbox.conversation-attributes.placeholder';
  }

  @action windowResized() {
    this.clientHeight = document.body.clientHeight;
  }

  @action editValue(event: Event) {
    if (this.linkWasClicked && event.type === 'focus') {
      this.linkWasClicked = false;
      return;
    }

    if (event.target instanceof HTMLAnchorElement && event.type === 'mousedown') {
      this.linkWasClicked = true;

      return;
    }

    this.args.edit && this.args.edit(event);
  }

  get heightStyle() {
    if (!this.args.isDescription) {
      return '';
    }

    return `max-height: ${this.clientHeight * 0.35}px;`;
  }

  get linkifiedValue() {
    if (this.args.linkifiedValue !== undefined) {
      return this.args.linkifiedValue;
    }
    return this.escapeHtml(this.args.value)?.replace(URL_REGEX, URL_REPLACEMENT);
  }

  escapeHtml(unsafe: string | null | undefined) {
    return unsafe
      ?.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::TicketAttribute::LinkifiedAttribute': typeof LinkifiedAttribute;
    'inbox2/ticket-attribute/linkified-attribute': typeof LinkifiedAttribute;
  }
}
